import { useMemo } from "react";

import { IntlShape } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";

import SandboxOption from "@app/scene/settings/component/Sandbox/type/SandboxOption";
import { getEntityTypeDisplayName } from "@app/util/ui";

import OptionEntityTypes from "../component/SandboxItems/util/OptionEntityTypes";

const useSandboxOptions = (intl: IntlShape, doesIntegrationExist: boolean): SandboxOption[] =>
  useMemo(() => {
    let options: SandboxOption[] = [
      {
        disabled: !doesIntegrationExist,
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.integrationConfiguration",
          defaultMessage: "Integration configuration",
          description: "Sandbox item - Integration configuration",
        }),
        nameHint: doesIntegrationExist
          ? intl.formatMessage({
              id: "settings.sandboxEnvironment.editPane.items.integrationConfiguration.nameHint",
              defaultMessage:
                "Note that by selecting to clone your integration configurations, you must also clone users and custom fields",
              description: "Sandbox item - Integration configuration name Hint",
            })
          : intl.formatMessage({
              id: "settings.sandboxEnvironment.editPane.items.integrationConfiguration.nameHint.noIntegration",
              defaultMessage:
                "You must have an active integration to clone a configuration into sandbox",
              description:
                "Sandbox item - Integration configuration name Hint with no integrations",
            }),
        namePath: "integrations",
      },
      {
        entityTypes: OptionEntityTypes,
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.currentWorkflows",
          defaultMessage: "Current workflows",
          description: "Sandbox item - Current workflows",
        }),
        nameHint: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.currentWorkflows.nameHint",
          defaultMessage:
            "Note that by selecting to clone workflows, you must also clone custom fields and saved filters",
          description: "Sandbox item - Current workflows name Hint",
        }),
        namePath: "workflows",
      },
      {
        dependencies: ["integrations"],
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.users",
          defaultMessage: "Users",
          description: "Sandbox item - Users",
        }),
        namePath: "users",
      },
      {
        dependencies: ["integrations", "workflows"],
        entityTypes: OptionEntityTypes,
        getEntityName: (entityType: EntityType) =>
          intl.formatMessage(
            {
              id: "settings.sandboxEnvironment.editPane.items.currentWorkflow.entity",
              defaultMessage: "{entityType} fields",
              description: "Sandbox item - Entity Custom fields",
            },
            {
              entityType: getEntityTypeDisplayName(intl, entityType, {
                lowercase: false,
                plural: false,
              }),
            }
          ),
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.customFields",
          defaultMessage: "Custom fields",
          description: "Sandbox item - Custom fields",
        }),
        namePath: "customFields",
      },
      {
        dependencies: ["workflows"],
        entityTypes: OptionEntityTypes,
        getEntityName: (entityType: EntityType) =>
          intl.formatMessage(
            {
              id: "settings.sandboxEnvironment.editPane.items.savedFilters.entity",
              defaultMessage: "{entityType} saved filters",
              description: "Sandbox item - Entity saved filters",
            },
            {
              entityType: getEntityTypeDisplayName(intl, entityType, {
                lowercase: false,
                plural: false,
              }),
            }
          ),
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.savedFilters",
          defaultMessage: "Saved Filters",
          description: "Sandbox item - Custom fields",
        }),
        namePath: "savedFilters",
      },
      {
        entityTypes: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
        getEntityName: (entityType: EntityType) => {
          return intl.formatMessage(
            {
              id: "settings.sandboxEnvironment.editPane.items.savedFilters.groups",
              defaultMessage: "{entityType} Groups",
              description: "Sandbox item - Entity Groups",
            },
            {
              entityType: getEntityTypeDisplayName(intl, entityType, {
                lowercase: false,
                plural: false,
              }),
            }
          );
        },
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.groups",
          defaultMessage: "Groups",
          description: "Sandbox item - Groups",
        }),
        namePath: "groups",
      },
      {
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.territories",
          defaultMessage: "Territories",
          description: "Sandbox item - Territories",
        }),
        namePath: "territories",
      },
      {
        entityTypes: OptionEntityTypes,
        getEntityName: (entityType: EntityType) =>
          intl.formatMessage(
            {
              id: "settings.sandboxEnvironment.editPane.items.layouts.entity",
              defaultMessage: "{entityType} layouts",
              description: "Sandbox item - Entity layouts",
            },
            {
              entityType: getEntityTypeDisplayName(intl, entityType, {
                lowercase: false,
                plural: true,
              }),
            }
          ),
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.layouts",
          defaultMessage: "Layouts",
          description: "Sandbox item - Layouts",
        }),
        namePath: "layouts",
      },
      {
        name: intl.formatMessage({
          id: "settings.sandboxEnvironment.editPane.items.crmActivityTypes",
          defaultMessage: "Custom activity types",
          description: "Sandbox item - Custom activity types",
        }),
        namePath: "crmActivityTypes",
      },
    ];
    options = options.map((option) => {
      const dependentOptions = options.filter(
        ({ dependencies }) => dependencies && dependencies.indexOf(option.namePath) !== -1
      );
      const checkValue = {
        [option.namePath]: option.getEntityName && option.entityTypes ? option.entityTypes : true,
      };
      const unCheckValue = {
        [option.namePath]: option.getEntityName ? [] : false,
      };
      dependentOptions.forEach((dependentOption) => {
        checkValue[dependentOption.namePath] =
          dependentOption.getEntityName && dependentOption.entityTypes
            ? dependentOption.entityTypes
            : true;
      });
      return {
        ...option,
        checkValue,
        unCheckValue,
      };
    });
    return options;
  }, [doesIntegrationExist, intl]);

export default useSandboxOptions;
